import { safeFormatDateTime } from "@/utils/formatDate";
import { ItineraryCard } from "@/features/purchase-ticket/booking/ItineraryCard";
import { PriceBreakdown } from "@/features/purchase-ticket/booking/PriceBreakDown";
import { Button } from "@/components/ui/button";
import { useActionBarHeight } from "@/hooks/use-action-bar-height";
import { useLocation, useNavigate } from "react-router-dom";
import { formatCurrency } from "@/utils/formatCurrency";
import { useEffect, useState } from "react";
import type { PurchaseSummaryData } from "@/types/purchasesummary";
import { useAuthStore } from "@/stores/auth.store";
import { GetPurchaseSummary } from "@/services/purchase.service";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, FileText, Loader2 } from "lucide-react";
import BookingConfirmationSkeleton from "./BookingConfimationSkeleton";
import { handleDownloadConfirmation } from "@/hooks/downloadConfirmation";
import { downloadTransactionReceipt } from "@/hooks/downloadTransactionReceipt";

export default function BookingConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [downloadingBooking, setDownloadingBooking] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  const paymentId = location.state?.paymentId;
  const [purchaseSummary, setPurchaseSummary] = useState<PurchaseSummaryData | null>(null);

  // Expose the fixed action bar height so the layout's bottom-hero sits above it
  const footerRef = useActionBarHeight<HTMLElement>();

  useEffect(() => {
    const fetchPurchaseSummary = async () => {
      if (!paymentId) {
        toast.error("Payment ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const agentCompanySellerId = useAuthStore.getState().user?.agentCompanySellerId ?? "";

        const delay = new Promise((resolve) => {
          setTimeout(resolve, 2000);
        });

        const [response] = await Promise.all([
          GetPurchaseSummary({
            agentCompanySellerId,
            paymentId,
          }),
          delay,
        ]);

        setPurchaseSummary(response.data);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to get purchase summary.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseSummary();
  }, [paymentId]);

  const hanldeTicketAgain = () => {
    navigate("/purchase-ticket");
  };

  const hanldeViewBooking = () => {
    navigate(`/booking-history/${paymentId}`);
  };

  const handleDownloadBooking = async () => {
    if (!paymentId || downloadingBooking || downloadingReceipt) return;

    try {
      setDownloadingBooking(true);

      await handleDownloadConfirmation(paymentId);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download booking confirmation.",
      );
    } finally {
      setDownloadingBooking(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!paymentId || downloadingReceipt || downloadingBooking) return;

    try {
      setDownloadingReceipt(true);

      await downloadTransactionReceipt(paymentId);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download booking receipt.",
      );
    } finally {
      setDownloadingReceipt(false);
    }
  };

  if (loading) {
    return <BookingConfirmationSkeleton />;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pb-40 pt-4 sm:px-6 lg:px-8 md:max-w-5xl md:pb-16">
      <div className="flex flex-col items-center text-center p-2">
        <CheckCircle2 size={48} className="text-green-700" aria-hidden="true" />
        <h1 className="mt-2 text-lg font-semibold text-heading">
          Successfully Booked
        </h1>
        <p className="mt-2 text-sm font-medium text-secondary-foreground">
          Confirmation email sent to{" "}
          {purchaseSummary?.contactDetails?.contactEmail ?? "-"}.
          <br />
          Tickets deducted from inventory
        </p>
      </div>

      <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
        Booking Summary
      </h2>
      <section className="mt-2 rounded-2xl bg-card px-4 py-6 md:p-6 shadow-xl">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-muted">Booking no.</p>
            <p className="text-sm mt-1 font-medium text-muted-foreground">
              {purchaseSummary?.bookingNo ?? "-"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs font-medium text-muted">Payment Method</p>
            <p className="text-sm mt-1 font-medium text-muted-foreground capitalize">
              {purchaseSummary?.paymentMethod ?? "-"}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-4 mt-4">
          <div className="text-left">
            <p className="text-xs font-medium text-muted">Total Amount</p>
            <p className="text-sm mt-1 font-medium text-muted-foreground">
              {formatCurrency(purchaseSummary?.totalAmount ?? 0)}
            </p>
          </div>

          <div className="text-right">
            <p className="text-xs font-medium text-muted">Purchase Date/Time</p>
            <p className="text-sm mt-1 font-medium text-muted-foreground">
              {safeFormatDateTime(purchaseSummary?.purchaseDateTime) ?? "-"}
            </p>
          </div>
        </div>
      </section>

      <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
        Trip Details
      </h2>
      <div className="mt-2">
        <ItineraryCard
          routeName={purchaseSummary?.tripDetails.routeName ?? "-"}
          departureDate={purchaseSummary?.tripDetails.departureDateTime}
          returnDate={purchaseSummary?.tripDetails.returnDateTime}
        />
      </div>

      <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
        Contact Details
      </h2>
      <section className="mt-2 rounded-2xl bg-card px-4 py-6 md:p-6 shadow-xl">
        <div className="md:grid md:grid-cols-3 md:items-center md:gap-4">
          <div>
            <p className="text-xs font-medium text-muted">Contact name</p>
            <p className="text-sm font-medium text-muted-foreground">
              {purchaseSummary?.contactDetails.contactName ?? "-"}
            </p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 md:contents">
            <div className="md:text-start">
              <p className="text-xs font-medium text-muted">Email Address</p>
              <p className="text-sm font-medium text-muted-foreground">
                {purchaseSummary?.contactDetails.contactEmail ?? "-"}
              </p>
            </div>
            <div className="text-right md:text-start">
              <p className="text-xs font-medium text-muted">Contact no.</p>
              <p className="text-sm font-medium text-muted-foreground">
                {purchaseSummary?.contactDetails.contactNo ?? "-"}
              </p>
            </div>
          </div>
        </div>
      </section>

      <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
        Price Breakdown
      </h2>
      <PriceBreakdown
        priceBreakdown={purchaseSummary?.priceBreakdown ?? []}
        grandTotal={purchaseSummary?.totalAmount ?? 0}
        className="mt-2"
      />

      <h2 className="mt-8 text-lg font-semibold text-secondary-foreground">
        Current Balance
      </h2>

      {purchaseSummary?.currentBalance?.map((balance) => {
        const balances = [
          {
            label: "Adult (Malaysian)",
            value: balance.adult,
          },
          {
            label: "Child (Malaysian)",
            value: balance.child,
          },
          {
            label: "Adult (Foreigner)",
            value: balance.adultForeigner,
          },
          {
            label: "Child (Foreigner)",
            value: balance.childForeigner,
          },
          {
            label: "Local",
            value: balance.local,
          },
        ];

        return (
          <section className="mt-2 rounded-2xl bg-card px-4 py-6 shadow-xl md:p-6">
            <div key={balance.routeName} className="not-last:mb-6">
              <p className="flex items-center gap-1 text-sm font-semibold text-secondary-foreground">
                <span className="flex items-center gap-2">
                  {balance.routeName?.split("-").map((part, index, parts) => (
                    <span key={part} className="flex items-center gap-2">
                      <span>{part.trim()}</span>

                      {index < parts.length - 1 && (
                        <ArrowRight className="h-4 w-4 shrink-0" />
                      )}
                    </span>
                  ))}
                </span>
              </p>

              <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
                {balances.map((item) => (
                  <div key={item.label}>
                    <p className="text-xs font-medium leading-tight text-muted">
                      <span>{item.label.split(" (")[0]}</span>
                      {item.label.includes(" (") && (
                        <span className="block md:inline">
                          ({item.label.split(" (")[1]}
                        </span>
                      )}
                    </p>

                    <p className="mt-1 text-2xl font-semibold tabular-nums text-heading">
                      {item.value ?? 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      <div className="my-8 flex flex-col gap-8 md:flex-row">
        <div className="flex-1">
          <Button
            type="button"
            variant="outline"
            className="w-full border-2 border-primary text-primary bg-card gap-2.5 hover:bg-primary-soft"
            onClick={handleDownloadBooking}
            disabled={downloadingBooking}
          >
            {downloadingBooking ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <FileText size={20} />
                Download Booking Confirmation
              </>
            )}
          </Button>
        </div>
        <div className="flex-1">
          <Button
            type="button"
            variant="outline"
            className="w-full border-2 border-primary bg-card text-primary gap-2.5 hover:bg-primary-soft"
            onClick={handleDownloadReceipt}
            disabled={downloadingReceipt}
          >
            {downloadingReceipt ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <FileText size={20} />
                Download Transaction Receipt
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="mt-8 flex-col gap-4 hidden md:flex">
        <Button
          type="button"
          variant="secondary"
          className="w-full cursor-pointer"
          onClick={hanldeViewBooking}
        >
          View Booking
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="w-full cursor-pointer"
          onClick={hanldeTicketAgain}
        >
          Purchase Ticket Again
        </Button>
      </div>

      <footer
        ref={footerRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card md:hidden"
      >
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer"
            onClick={hanldeViewBooking}
          >
            View Booking
          </Button>

          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer"
            onClick={hanldeTicketAgain}
          >
            Purchase Ticket Again
          </Button>
        </div>
      </footer>
    </div>
  );
}
